import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowDown } from 'lucide-react';
import useScrollReveal from '../hooks/useScrollReveal';
import useSeo from '../hooks/useSeo';
import { IMAGES, ABOUT_PARAGRAPHS, STATS, PILLARS, VALUES, COMPANY, FOOTPRINT } from '../mock';

const Home = () => {
  useScrollReveal();
  useSeo({ title: 'Bringing Botswana to the World', description: 'LRG Holdings is a Botswana based investment and development company founded in 2014, connecting Botswana\u2019s industries and people to global opportunity across diamonds, leather, education and community development.', path: '/', image: '/images/edu-ub-robinson.jpeg' });

  return (
    <div>
      {/* HERO */}
      <section className="relative h-screen min-h-[640px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <img src={IMAGES.heroSavanna} alt="Botswana savanna at golden hour, the landscape LRG Holdings calls home" className="w-full h-full object-cover kenburns" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#071627]/70 via-[#071627]/45 to-[#071627]/85" />
        </div>
        <div className="relative z-10 text-center px-6">
          <p className="eyebrow-light mb-6 reveal is-visible">LRG Holdings &middot; Est. {COMPANY.established}</p>
          <h1 className="font-serif text-[#f7f4ee] text-5xl md:text-7xl lg:text-8xl font-medium leading-[1.05] max-w-5xl mx-auto">
            Bringing Botswana<br />to the World
          </h1>
          <div className="gold-rule mx-auto my-8" />
          <p className="text-white/75 max-w-xl mx-auto font-light text-lg leading-relaxed">
            An investment and development company connecting Botswana&rsquo;s industries and people to global opportunity across diamonds, leather, education and community development.
          </p>
          <div className="flex flex-wrap gap-4 justify-center mt-10">
            <Link to="/diamonds" className="btn-gold">Our Sectors <ArrowRight size={16} /></Link>
            <Link to="/contact" className="btn-outline btn-outline-light">Get in Touch</Link>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/50 animate-bounce">
          <ArrowDown size={22} />
        </div>
      </section>

      {/* ABOUT */}
      <section className="py-28 bg-[#f7f4ee]">
        <div className="container-lg grid lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-5 reveal">
            <p className="eyebrow mb-4">About LRG Holdings</p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#0a1f38] leading-tight">
              A bridge between Botswana&rsquo;s promise and global markets.
            </h2>
            <div className="gold-rule mt-8" />
          </div>
          <div className="lg:col-span-7 reveal">
            <div className="space-y-6 text-[#3a3f47] leading-relaxed text-[1.05rem] font-light">
              {ABOUT_PARAGRAPHS.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
            <Link to="/contact" className="inline-flex items-center gap-2 mt-8 text-[#a3813f] tracking-[0.18em] uppercase text-xs font-semibold group">
              Partner With Us <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="relative bg-[#060d18] overflow-hidden" data-testid="founder-section">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_50%,rgba(18,49,79,0.55),transparent_60%)]" />
        <div className="relative container-lg grid lg:grid-cols-12 gap-0 lg:gap-16 items-center">
          <div className="lg:col-span-6 relative py-16 lg:py-24 reveal">
            <div className="absolute -left-6 top-24 hidden lg:block font-serif italic text-[#c6a15b]/15 text-[11rem] leading-none select-none">LR</div>
            <div className="relative border border-[#c6a15b]/30 p-3">
              <div className="absolute -top-px -left-px w-10 h-10 border-t-2 border-l-2 border-[#c6a15b]" />
              <div className="absolute -bottom-px -right-px w-10 h-10 border-b-2 border-r-2 border-[#c6a15b]" />
              <div className="img-zoom"><img src={IMAGES.lefika} alt="Lefika Ragontse, Founder of LRG Holdings, addressing the University of Botswana" className="w-full object-cover" /></div>
            </div>
            <p className="text-white/40 text-xs tracking-[0.18em] uppercase mt-5">Lefika Ragontse addressing the University of Botswana</p>
          </div>
          <div className="lg:col-span-6 flex flex-col justify-center py-16 lg:py-24 reveal" style={{ transitionDelay: '150ms' }}>
            <p className="eyebrow eyebrow-line eyebrow-light mb-8">The Founder</p>
            <h2 className="font-serif text-[#f7f4ee] text-5xl md:text-6xl leading-[0.98]">Lefika <span className="italic text-[#d9bd82]">Ragontse</span></h2>
            <p className="text-[#c6a15b] text-xs tracking-[0.28em] uppercase mt-5">Founder and Chairman, LRG Holdings</p>
            <blockquote className="font-serif text-2xl md:text-[1.75rem] text-[#f7f4ee]/90 leading-snug mt-10 pl-6 border-l-2 border-[#c6a15b]">
              &ldquo;Botswana has always had the brilliance. My work is to build the bridges that carry it to the world, and to bring the world home to Botswana.&rdquo;
            </blockquote>
            <p className="text-white/60 font-light leading-relaxed text-lg mt-8">
              A four time NCAA Champion and sixteen time Botswana National Champion, Lefika Ragontse has moved between Gaborone, American collegiate sport and the world&rsquo;s trading floors with the same conviction: Botswana&rsquo;s people and resources belong on the global stage. Through LRG Holdings he has convened Nobel Laureates, world class universities and international capital around one idea: Bringing Botswana to the World.
            </p>
            <div className="grid grid-cols-3 ledger-grid dark mt-12">
              {[['4x', 'NCAA Champion'], ['16x', 'Botswana National Champion'], ['2014', 'Founded LRG Holdings']].map(([v, l]) => (
                <div key={l} className="p-5"><p className="font-serif text-4xl text-[#d9bd82]">{v}</p><p className="text-white/45 text-[10px] tracking-[0.18em] uppercase mt-2">{l}</p></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-[#0a1f38] py-20">
        <div className="container-lg grid grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-8">
          {STATS.map((s, i) => (
            <div key={i} className="text-center reveal" style={{ transitionDelay: `${i * 90}ms` }}>
              <p className="font-serif text-5xl md:text-6xl text-[#c6a15b] font-medium">{s.value}</p>
              <p className="text-white/60 text-xs tracking-[0.2em] uppercase mt-3">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FOOTPRINT */}
      <section className="py-28 bg-[#f7f4ee]" data-testid="footprint-section">
        <div className="container-lg">
          <div className="grid lg:grid-cols-12 gap-8 items-end mb-14 reveal">
            <div className="lg:col-span-7">
              <p className="eyebrow mb-5">Global Footprint</p>
              <h2 className="font-serif text-4xl md:text-6xl text-[#0a1f38] leading-[1.02]">Three continents, <span className="italic text-[#a3813f]">one home.</span></h2>
            </div>
            <p className="lg:col-span-5 text-[#3a3f47] font-light leading-relaxed text-lg">From Gaborone to Johannesburg, Baltimore to Chicago, and the tanneries of Tuscany, LRG Holdings works across Africa, North America and Europe with Botswana always at the centre.</p>
          </div>
          <div className="grid md:grid-cols-3 ledger-grid">
            {FOOTPRINT.map((f, i) => (
              <div key={f.continent} className="p-10 reveal" style={{ transitionDelay: `${i * 100}ms` }} data-testid={`footprint-${i}`}>
                <div className="flex items-center justify-between"><span className="numeral text-3xl">0{i + 1}</span><span className="text-[10px] tracking-[0.18em] uppercase text-[#a3813f]">{f.places.length} locations</span></div>
                <h3 className="font-serif text-4xl text-[#0a1f38] mt-8 mb-8">{f.continent}</h3>
                <ul className="divide-y divide-[#0a1f38]/10">
                  {f.places.map((pl) => (
                    <li key={pl.city} className="py-4">
                      <p className="text-[#0a1f38] font-medium">{pl.city}</p>
                      <p className="text-[#6b6f76] font-light text-sm leading-relaxed mt-1">{pl.role}</p>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PILLARS / SECTORS */}
      <section className="py-28 bg-[#efe9df]">
        <div className="container-lg">
          <div className="text-center max-w-2xl mx-auto mb-16 reveal">
            <p className="eyebrow mb-4">Our Sectors</p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#0a1f38]">Four pillars, one vision</h2>
            <p className="text-[#6b6f76] mt-5 font-light">Each division advances Botswana&rsquo;s standing on the world stage while creating meaningful opportunity at home.</p>
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-8">
            {PILLARS.map((p, i) => (
              <Link to={p.to} key={p.id} className="group reveal bg-white shadow-sm hover:shadow-2xl hover:shadow-black/10 transition-all duration-500 hover:-translate-y-2" style={{ transitionDelay: `${i * 100}ms` }}>
                <div className="img-zoom relative h-60">
                  <img src={IMAGES[p.image]} alt={p.title} className="w-full h-full object-cover" style={{ objectPosition: p.focus || 'center' }} />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#071627]/70 to-transparent" />
                  <span className="absolute top-5 left-5 font-serif text-[#c6a15b] text-3xl">{p.number}</span>
                </div>
                <div className="p-8">
                  <p className="eyebrow mb-2">{p.subtitle}</p>
                  <h3 className="font-serif text-3xl text-[#0a1f38] mb-3">{p.title}</h3>
                  <p className="text-[#6b6f76] font-light leading-relaxed text-[0.95rem]">{p.blurb}</p>
                  <span className="inline-flex items-center gap-2 mt-6 text-[#a3813f] tracking-[0.18em] uppercase text-xs font-semibold">
                    Learn More <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* EDUCATION HIGHLIGHT */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <img src={IMAGES.eduUbRobinson} alt="Professor James A. Robinson with University of Botswana students and faculty" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-[#071627]/85" />
        </div>
        <div className="relative z-10 container-lg text-center max-w-3xl mx-auto reveal">
          <p className="eyebrow-light mb-5">Flagship Initiative</p>
          <h2 className="font-serif text-4xl md:text-5xl text-[#f7f4ee] leading-tight">
            LRG Holdings bridges the University of Botswana and the University of Chicago.
          </h2>
          <p className="text-white/70 font-light mt-6 leading-relaxed">
            LRG Holdings initiated and secured the landmark partnership between the University of Botswana and the University of Chicago Harris School of Public Policy, bringing the two institutions together to create international academic pathways for Botswana&rsquo;s future leaders.
          </p>
          <Link to="/education" className="btn-outline btn-outline-light mt-10">Find Out More <ArrowRight size={16} /></Link>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-28 bg-[#f7f4ee]">
        <div className="container-lg">
          <div className="grid md:grid-cols-3 gap-12">
            {VALUES.map((v, i) => (
              <div key={i} className="reveal" style={{ transitionDelay: `${i * 100}ms` }}>
                <p className="font-serif text-6xl text-[#c6a15b]/30">0{i + 1}</p>
                <h3 className="font-serif text-2xl text-[#0a1f38] mt-3 mb-3">{v.title}</h3>
                <div className="gold-rule mb-5" />
                <p className="text-[#6b6f76] font-light leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
